// conditionReportsClient — AUTHENTICATED staff client for the Phase 4 move-in
// condition-report TRACKING surface. Uses the shared `api` helper (Bearer token
// + 401 refresh-retry).
//
// Wire shapes mirror apps/api/src/routes/conditionReportsReview.ts. Read/ops
// only — listing never triggers a send; retry re-arms a send-path exception and
// resolve dismisses one. Neither changes the dormant send posture.

import { api } from './api';
import type { AppfolioSyncDisplay } from './appfolioSync';
export type { AppfolioSyncDisplay };

export type ConditionReportStatus =
  | 'polling'
  | 'ready'
  | 'sending'
  | 'sent'
  | 'gary_pending'
  | 'completed'
  | 'exception';

export type ConditionReportExceptionCode =
  | 'ambiguous_unit'
  | 'not_linked'
  | 'no_tenants'
  | 'tenant_no_email'
  | 'stuck_inspection'
  | 'render_failed'
  | 'docuseal_failed';

export type SignerStatus = 'pending' | 'sent' | 'opened' | 'completed' | 'declined';

export interface ConditionReportSignerProgress {
  role: string;
  name?: string;
  email: string;
  status: SignerStatus;
  isCountersigner: boolean;
  completedAt?: string;
  openedAt?: string;
}

export interface ConditionReportTrackingRow {
  zInspectorDocumentId: string;
  title: string;
  status: ConditionReportStatus;
  /** Human property/unit line (street address, else inspection title). */
  propertyLabel: string;
  /** Comma-joined resident names, when resolved. */
  residentNames?: string;
  accountingId?: string;
  propertyId?: string;
  unitId?: string;
  signingLabel: string;
  residentsSigned: number;
  residentsTotal: number;
  countersignerSigned: boolean;
  /** Numeric DocuSeal submission id, when this report has been sent. */
  docusealSubmissionId?: number;
  /** Staff/admin DocuSeal submission link (deep link into the DocuSeal console). */
  docusealUrl?: string;
  signers: ConditionReportSignerProgress[];
  firstSeenAt: string;
  lastPolledAt?: string;
  updatedAt: string;
  completionNotifiedAt?: string;
  appfolioSync?: AppfolioSyncDisplay;
}

export interface ConditionReportExceptionRow {
  zInspectorDocumentId: string;
  title: string;
  /** Human property/unit line (street address, else inspection title). */
  propertyLabel: string;
  /** Comma-joined resident names, when resolved before the exception. */
  residentNames?: string;
  exceptionCode: ConditionReportExceptionCode;
  exceptionReason?: string;
  sendError?: string;
  accountingId?: string;
  unitId?: string;
  retryable: boolean;
  firstSeenAt: string;
  updatedAt: string;
  appfolioSync?: AppfolioSyncDisplay;
}

export interface ConditionReportExceptionGroup {
  exceptionCode: ConditionReportExceptionCode;
  count: number;
  rows: ConditionReportExceptionRow[];
}

export interface ConditionReportsSummary {
  count: number;
  tracking: ConditionReportTrackingRow[];
  exceptions: ConditionReportExceptionGroup[];
  exceptionCount: number;
}

/** The shape of a single lifecycle record returned by retry/resolve. */
export interface ConditionReportRecord {
  zInspectorDocumentId: string;
  title: string;
  status: ConditionReportStatus;
  exceptionCode?: ConditionReportExceptionCode;
  exceptionReason?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  resolvedReason?: string;
  updatedAt: string;
}

export async function listConditionReports(): Promise<ConditionReportsSummary> {
  return api.get<ConditionReportsSummary>('/api/condition-reports');
}

export async function retryConditionReport(
  zInspectorDocumentId: string,
): Promise<ConditionReportRecord> {
  const r = await api.post<{ record: ConditionReportRecord }>(
    `/api/condition-reports/${encodeURIComponent(zInspectorDocumentId)}/retry`,
  );
  return r.record;
}

export async function resolveConditionReport(
  zInspectorDocumentId: string,
  reason: string,
): Promise<ConditionReportRecord> {
  const r = await api.post<{ record: ConditionReportRecord }>(
    `/api/condition-reports/${encodeURIComponent(zInspectorDocumentId)}/resolve`,
    { reason },
  );
  return r.record;
}
